import { ChangeDetectorRef, computed, Directive, effect, input, inject, linkedSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { V2DropdownComponent } from './v2-dropdown.component';

/**
 * Adds a built-in search functionality to `coin-v2-dropdown`.
 *
 * @example
 * ```html
 * <coin-v2-dropdown [dataSource]="items" #search="dropdownSearch">
 *   @for (item of search.results(); track item.id) {
 *     <coin-v2-dropdown-option [value]="item">{{ item.name }}</coin-v2-dropdown-option>
 *   }
 * </coin-v2-dropdown>
 * ```
 *
 * `dataSource` accepts either a fixed array (client-side filtering) or a search function (server-side filtering):
 * ```ts
 * items = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }];
 * searchFn = (query: string) => this.api.search(query);
 * ```
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'coin-v2-dropdown[dataSource]',
  standalone: true,
  exportAs: 'dropdownSearch'
})
export class DropdownSearchDirective<T> {
  private readonly dropdown = inject<V2DropdownComponent<T>>(V2DropdownComponent);
  private readonly cd = inject(ChangeDetectorRef);

  /** Either a search function or a fixed array of values; optional so `results()` can be read before it is bound */
  readonly dataSource = input<T[] | ((s: string) => Observable<T[]>)>();
  /** Search text used until the user types, e.g. to keep a preselected value in a paged result */
  readonly initialSearch = input('');

  constructor() {
    effect(() => {
      const dataSource = this.dataSource();
      if (Array.isArray(dataSource)) {
        this.dropdown.searchDebounce.set(0);
        this.cd.markForCheck();
      }
    });
    effect(() => {
      // An array source never loads, so leave a bound [loading] untouched
      if (Array.isArray(this.dataSource())) return;
      this.dropdown.loading.set(this.remoteResults.isLoading());
      this.cd.markForCheck();
    });
  }

  // Every emission refetches, also an unchanged text, so `resetSearch()` reloads a search function that reads outside state
  private readonly typedSearch = toSignal(this.dropdown.search, { equal: () => false });
  private readonly search = computed(() => this.typedSearch() ?? this.initialSearch(), { equal: () => false });

  private readonly remoteResults = rxResource({
    params: () => {
      const dataSource = this.dataSource();
      return typeof dataSource === 'function' ? { search: this.search(), dataSource } : undefined;
    },
    stream: ({ params: { search, dataSource } }) => dataSource(search)
  });

  // Keeps the previous options visible while a new search loads or after it fails
  private readonly lastRemoteResults = linkedSignal<T[] | undefined, T[] | undefined>({
    source: () => (this.remoteResults.hasValue() ? this.remoteResults.value() : undefined),
    computation: (value, previous) => value ?? previous?.value
  });

  public readonly results = computed(() => {
    const dataSource = this.dataSource();
    if (!Array.isArray(dataSource)) {
      return this.lastRemoteResults();
    }

    const lowerCaseSearch = this.search().toLowerCase();
    return dataSource.filter(value => this.searchObjectValuesDeep(value, lowerCaseSearch));
  });

  /** For primitives, returns true if the converted string value contains the search text. <br>
   * For objects, returns true if any deeply nested primitive value contains the search text. */
  private searchObjectValuesDeep(obj: unknown, search: string): boolean {
    if (obj === null || obj === undefined) {
      return false;
    }
    if (Array.isArray(obj)) {
      return obj.some(value => this.searchObjectValuesDeep(value, search));
    }
    if (typeof obj === 'object') {
      return Object.values(obj).some(value => this.searchObjectValuesDeep(value, search));
    }
    return obj.toString().toLowerCase().includes(search);
  }
}
